/**
 * Cost pips. UI_UX section 4: circles, generic grey with a number, faction pips
 * filled with the faction color and its glyph in dark ink.
 *
 * A cost of {generic: 2, btc: 1} renders as one grey "2" and one orange BTC
 * pip, which is how a player reads "two of anything plus one Bitcoin".
 */

import type { ReactNode } from 'react';
import type { Chain, Cost } from '@/engine';
import { CHAINS } from '@/engine';
import { FactionGlyph } from '@/ui/icons/FactionGlyph';
import styles from './CardFrame.module.css';

export interface CostPipsProps {
  cost: Cost & { x?: boolean };
  /** Glyph size inside a faction pip, in px before the frame scale. */
  glyphSize?: number;
  className?: string;
}

export function CostPips({ cost, glyphSize = 9, className }: CostPipsProps) {
  const pips: ReactNode[] = [];

  if (cost.x) {
    pips.push(
      <span key="x" className={styles.pip} title="X, paid with generic Gas">
        X
      </span>,
    );
  }

  const generic = cost.generic ?? 0;
  if (generic > 0) {
    pips.push(
      <span key="generic" className={styles.pip} title={`${generic} generic Gas`}>
        {generic}
      </span>,
    );
  }

  for (const chain of CHAINS) {
    const n = cost[chain] ?? 0;
    for (let i = 0; i < n; i++) {
      pips.push(<ChainPip key={`${chain}-${i}`} chain={chain} glyphSize={glyphSize} />);
    }
  }

  if (pips.length === 0) {
    pips.push(
      <span key="free" className={styles.pip} title="No Gas cost">
        0
      </span>,
    );
  }

  return (
    <span className={[styles.pips, className].filter(Boolean).join(' ')} aria-label={costLabel(cost)}>
      {pips}
    </span>
  );
}

function ChainPip({ chain, glyphSize }: { chain: Chain; glyphSize: number }) {
  return (
    <span
      className={`${styles.pip} ${styles.pipFaction}`}
      style={{ ['--pip-color' as string]: `var(--${chain})` }}
      title={`1 ${chain.toUpperCase()} Gas`}
    >
      <FactionGlyph faction={chain} size={glyphSize} color="var(--bg-0)" />
    </span>
  );
}

/** Accessible description of a cost, e.g. "2 generic, 1 BTC". */
export function costLabel(cost: Cost & { x?: boolean }): string {
  const parts: string[] = [];
  if (cost.x) parts.push('X');
  if (cost.generic) parts.push(`${cost.generic} generic`);
  for (const chain of CHAINS) {
    const n = cost[chain] ?? 0;
    if (n > 0) parts.push(`${n} ${chain.toUpperCase()}`);
  }
  return parts.length === 0 ? 'No cost' : `Cost ${parts.join(', ')}`;
}
